import React, { useEffect, useState, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { 
  FileText, Plus, ChevronDown, Download, Printer, Search, 
  Package, Clock, CheckCircle, Archive, MoreHorizontal, X, 
  LayoutDashboard, ScrollText, ClipboardList, PlusCircle,
  Eye, Edit, Trash2, ChevronRight, FileJson, Truck
} from "lucide-react";
import { getRFQs, getRFPs, getBOQs, getPOs } from "../../api/OAprocurement-api";
import MasterSideBar from "../../components/MasterSideBar";
import Topbar from "../../components/TopbarNav";
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumCard from "../../components/common/PremiumCard";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import BOQForm from "../../components/operationsAssociate/CreateBOQ";
import CreateRFPForm from "../../components/operationsAssociate/CreateRFPs";
import CreateRFQ from "../../components/operationsAssociate/CreateRFQs";
import ViewFormModal from "../../components/operationsAssociate/ViewFormModal";
import ProposalForm from "../../components/operationsAssociate/ProposalForm";
import ViewProposals from "../../components/operationsAssociate/ViewProposals";
import PurchaseOrder from "../../components/operationsAssociate/PurchaseOrder";
import { exportRFQPDF } from "../../utils/exportpdf";
import { motion, AnimatePresence } from "framer-motion";

// Reusable Action Dropdown with premium styling
const ActionDropdown = ({ request, onView, onAddProposal, onViewProposals, onExportPDF, onCreatePurchaseOrder }) => {
  const [isOpen, setIsOpen] = useState(false);

  const getActions = () => {
    const base = [{ label: "View Details", onClick: onView, icon: Eye }];
    if (["RFQ", "RFP", "BOQ"].includes(request.type)) {
      return [
        ...base,
        { label: "Add Submission", onClick: onAddProposal, icon: PlusCircle },
        { label: "View Submissions", onClick: onViewProposals, icon: LayoutDashboard },
        ...(request.type === "RFQ" ? [{ label: "Export PDF", onClick: onExportPDF, icon: Download }] : []),
        { label: "Generate PO", onClick: onCreatePurchaseOrder, icon: FileJson }
      ];
    }
    return base;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-all shadow-sm active:scale-95 group"
      >
        Manage <ChevronDown size={14} className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
            <motion.div 
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute right-0 mt-3 w-64 bg-white rounded-[2rem] shadow-2xl border border-gray-100 z-20 py-4 overflow-hidden origin-top-right px-2"
            >
              <p className="px-5 py-3 text-[10px] font-black text-gray-300 uppercase tracking-widest border-b border-gray-50 mb-2">Administrative Actions</p>
              {getActions().map((action, index) => (
                <button
                  key={index}
                  onClick={() => { action.onClick(); setIsOpen(false); }}
                  className="w-full text-left px-5 py-3.5 text-[11px] font-black uppercase tracking-widest text-gray-500 hover:bg-orange-50 hover:text-orange-600 rounded-xl transition-all flex items-center justify-between group"
                >
                  <div className="flex items-center gap-3">
                    <action.icon size={14} className="group-hover:scale-110 transition-transform" />
                    {action.label}
                  </div>
                  <ChevronRight size={12} className="opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// Simplified PO Actions
const POActionDropdown = ({ po, onView, onDownload }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-3.5 bg-gray-50 text-gray-400 hover:bg-orange-600 hover:text-white rounded-2xl transition-all active:scale-95 shadow-sm border border-gray-100"
      >
        <MoreHorizontal size={18} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
            <motion.div 
              initial={{ opacity: 0, y: 5, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 5, scale: 0.95 }}
              className="absolute right-0 mt-3 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 z-20 py-2 px-2 overflow-hidden"
            >
              <button onClick={() => { onView(); setIsOpen(false); }} className="w-full text-left px-5 py-4 text-[10px] font-black uppercase tracking-widest text-gray-600 hover:bg-orange-50 hover:text-orange-600 rounded-xl transition-colors flex items-center gap-3"><Eye size={14}/> View Details</button>
              <button onClick={() => { onDownload(); setIsOpen(false); }} className="w-full text-left px-5 py-4 text-[10px] font-black uppercase tracking-widest text-gray-600 hover:bg-orange-50 hover:text-orange-600 rounded-xl transition-colors flex items-center gap-3"><Download size={14}/> Download Packet</button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// Main Modal Wrapper
const Modal = ({ isOpen, onClose, children, title, icon: Icon }) => (
  <AnimatePresence>
    {isOpen && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
        <motion.div 
          initial={{ scale: 0.95, opacity: 0, y: 20 }} 
          animate={{ scale: 1, opacity: 1, y: 0 }} 
          exit={{ scale: 0.95, opacity: 0, y: 20 }} 
          className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-gray-100"
        >
          <div className="px-10 py-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
            <div className="flex items-center gap-5">
              <div className="size-12 rounded-2xl bg-white shadow-sm text-orange-600 flex items-center justify-center border border-gray-50">
                <Icon size={24} strokeWidth={2.5} />
              </div>
              <h2 className="text-xs font-black text-gray-800 uppercase tracking-[0.2em]">{title}</h2>
            </div>
            <button onClick={onClose} className="p-4 bg-white hover:bg-gray-100 rounded-2xl transition-all active:scale-95 shadow-sm border border-gray-100"><X size={20} className="text-gray-400" /></button>
          </div>
          <div className="overflow-y-auto p-10 max-h-[calc(90vh-120px)] custom-scrollbar">{children}</div>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);

const ProcurementDashboard = () => {
  const [requests, setRequests] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [proposalModalOpen, setProposalModalOpen] = useState(false);
  const [viewProposalsModalOpen, setViewProposalsModalOpen] = useState(false);
  const [purchaseOrderModalOpen, setPurchaseOrderModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [pageState, setPageState] = useState({ RFQ: 1, RFP: 1, BOQ: 1 });
  const recordsPerPage = 5;

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const [rfqsRes, rfpRes, boqRes] = await Promise.all([getRFQs(), getRFPs(), getBOQs()]);
      setRequests([
        ...(Array.isArray(rfqsRes) ? rfqsRes.map(r => ({ ...r, type: "RFQ" })) : []),
        ...(Array.isArray(rfpRes) ? rfpRes.map(r => ({ ...r, type: "RFP" })) : []),
        ...(Array.isArray(boqRes) ? boqRes.map(r => ({ ...r, type: "BOQ" })) : [])
      ]);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  const fetchPurchaseOrders = useCallback(async () => {
    try {
      const posRes = await getPOs();
      setPurchaseOrders(Array.isArray(posRes) ? posRes : []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchPurchaseOrders();
  }, [fetchRequests, fetchPurchaseOrders]);

  const openProposalModal = (rfq) => { setSelectedRFQ(rfq); setProposalModalOpen(true); };
  const openViewProposalsModal = (rfq) => { setSelectedRFQ(rfq); setViewProposalsModalOpen(true); };
  const openPurchaseOrderModal = (req) => { setSelectedRFQ(req); setPurchaseOrderModalOpen(true); };

  const handleProposalSuccess = async () => { setProposalModalOpen(false); await fetchRequests(); };
  const handlePurchaseOrderSuccess = async () => { setPurchaseOrderModalOpen(false); await fetchRequests(); await fetchPurchaseOrders(); };

  const downloadPO = (po) => {
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`<html><body style="font-family:sans-serif;padding:40px;"><h1>PO: ${po.POId || po._id}</h1><p>Vendor: ${po.vendorName}</p><p>Amount: $${po.totalAmount}</p></body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <MasterSideBar 
        visible={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <Topbar onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto">
            
            {/* Centered Header */}
            <div className="flex flex-col items-center text-center mb-12">
                <PremiumHeader 
                  title="Operations" 
                  lastWordAccent="Associate" 
                  subtitle="Manage procurement pipelines, oversee vendor submissions, and generate authorized purchase orders." 
                />
                
                <div className="flex flex-wrap justify-center gap-4 mt-8">
                  {activeTab === "overview" ? (
                    <>
                      {[
                        { label: "RFQ", type: "rfq", icon: FileText },
                        { label: "RFP", type: "rfp", icon: FileJson },
                        { label: "PO", type: "boq", icon: ScrollText }
                      ].map(btn => (
                        <motion.button
                          key={btn.type}
                          whileHover={{ scale: 1.02, y: -2 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setActiveModal(btn.type)}
                          className="flex items-center gap-3 bg-gray-900 text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-orange-600 transition-all active:scale-95 shadow-xl shadow-gray-200"
                        >
                          <Plus size={16} strokeWidth={3} /> {btn.label}
                        </motion.button>
                      ))}
                    </>
                  ) : (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="h-[52px] flex items-center">
                       <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Order Management View</span>
                    </motion.div>
                  )}
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex justify-center mb-12">
              <div className="flex gap-2 bg-white p-2 rounded-2xl shadow-sm border border-gray-100">
                {[
                  { id: "overview", label: "Pipelines", icon: LayoutDashboard },
                  { id: "vendor", label: "Archive", icon: Archive }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-3 px-8 py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                      activeTab === tab.id ? "bg-orange-600 text-white shadow-xl shadow-orange-100" : "text-gray-400 hover:bg-gray-50 hover:text-orange-600"
                    }`}
                  >
                    <tab.icon size={14} /> {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <AnimatePresence mode="wait">
              {activeTab === "overview" ? (
                <motion.div 
                  key="pipelines"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-12"
                >
                  {/* Premium Stats Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <PremiumStatCard title="Total Inventory" value={requests.length} icon={<Package className="text-orange-600" />} iconBg="bg-orange-50" description="Consolidated requests" />
                    <PremiumStatCard title="Awaiting Review" value={requests.filter(r => r.status === "draft").length} icon={<Clock className="text-yellow-600" />} iconBg="bg-yellow-50" description="Action required" />
                    <PremiumStatCard title="Submission Phase" value={requests.filter(r => r.status === "submitted").length} icon={<CheckCircle className="text-emerald-600" />} iconBg="bg-emerald-50" description="Live pipelines" />
                    <PremiumStatCard title="Fulfilled/Closed" value={requests.filter(r => r.status === "closed").length} icon={<Archive className="text-gray-400" />} iconBg="bg-gray-100" description="Completed orders" />
                  </div>

                  {/* Grouped Tables */}
                  <div className="space-y-10">
                    {["RFQ", "RFP", "PO"].map((type) => {
                      const filtered = requests.filter(r => r.type === type);
                      if (filtered.length === 0) return null;
                      const currentRecords = filtered.slice((pageState[type] - 1) * recordsPerPage, pageState[type] * recordsPerPage);

                      return (
                        <PremiumCard 
                          key={type} 
                          title={`${type} Administrative Queue`} 
                          icon={<ClipboardList size={18} />}
                        >
                          <div className="overflow-x-auto p-2">
                            <table className="w-full text-left">
                              <thead>
                                <tr className="bg-gray-50/50">
                                  <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Reference</th>
                                  <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Project/Subject</th>
                                  <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Deadline</th>
                                  <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Status</th>
                                  <th className="px-8 py-5 text-right"></th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-50">
                                {currentRecords.map((req, i) => (
                                  <motion.tr 
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: i * 0.05 }}
                                    key={req._id} 
                                    className="hover:bg-gray-50/50 transition-all group"
                                  >
                                    <td className="px-8 py-6">
                                      <span className="text-xs font-black text-gray-800 bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-100 group-hover:bg-gray-900 group-hover:text-white group-hover:border-gray-900 transition-all">
                                        {req.RFQId || req.RFPId || req.BOQId || req._id.slice(-6)}
                                      </span>
                                    </td>
                                    <td className="px-8 py-6">
                                      <p className="text-sm font-black text-gray-800 mb-1 group-hover:text-orange-600 transition-colors uppercase tracking-tight">{req.projectName || req.notes || "Unnamed Request"}</p>
                                      <div className="flex items-center gap-2">
                                        <ChevronRight size={10} className="text-orange-500" />
                                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">Issued: {new Date(req.issueDate).toLocaleDateString()}</p>
                                      </div>
                                    </td>
                                    <td className="px-8 py-6">
                                       <div className="flex items-center gap-2 text-xs font-extrabold text-gray-500 uppercase tracking-tighter">
                                          <Clock size={12} className="text-gray-300" />
                                          {new Date(req.submissionDeadline).toLocaleDateString()}
                                       </div>
                                    </td>
                                    <td className="px-8 py-6">
                                      <span className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest ${
                                        req.status === "submitted" ? "bg-emerald-50 text-emerald-600 border border-emerald-100" : "bg-yellow-50 text-yellow-600 border border-yellow-100"
                                      }`}>{req.status}</span>
                                    </td>
                                    <td className="px-8 py-6 text-right">
                                      <ActionDropdown 
                                        request={req} 
                                        onView={() => { setSelectedRequest(req); setViewModalOpen(true); }}
                                        onAddProposal={() => openProposalModal(req)}
                                        onViewProposals={() => openViewProposalsModal(req)}
                                        onExportPDF={() => exportRFQPDF(req)}
                                        onCreatePurchaseOrder={() => openPurchaseOrderModal(req)}
                                      />
                                    </td>
                                  </motion.tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </PremiumCard>
                      );
                    })}
                  </div>
                </motion.div>
              ) : (
                <motion.div 
                  key="archive"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="animate-fadeIn"
                >
                  <PremiumCard title="Authorized Purchase Orders" icon={<ScrollText size={18} />}>
                    <div className="px-10 py-10 bg-gray-50/30 border-b border-gray-50 flex flex-col lg:flex-row justify-between items-center gap-6">
                      <div className="space-y-1 w-full lg:w-fit">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-2">Reference Archive Search</p>
                        <div className="relative group">
                          <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-orange-600 transition-colors" size={16} />
                          <input 
                            type="text" 
                            placeholder="Search by ID or Vendor Entity..." 
                            className="pl-14 pr-8 py-4 bg-white border border-gray-100 rounded-[1.5rem] text-xs font-bold w-full lg:w-[450px] focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 transition-all outline-none shadow-sm placeholder:text-gray-200" 
                          />
                        </div>
                      </div>
                      <div className="size-14 rounded-full bg-white border border-gray-100 flex items-center justify-center text-gray-300 shadow-sm">
                        <Archive size={20} />
                      </div>
                    </div>
                    <div className="overflow-x-auto p-2">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="bg-gray-50/50">
                            <th className="px-10 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">PO Reference</th>
                            <th className="px-10 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Vendor Entity</th>
                            <th className="px-10 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Logistics Point</th>
                            <th className="px-10 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Status</th>
                            <th className="px-10 py-5 text-right"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {purchaseOrders.length > 0 ? purchaseOrders.map((po, i) => (
                            <motion.tr 
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: i * 0.05 }}
                              key={po._id} 
                              className="hover:bg-gray-50/50 transition-all group"
                            >
                              <td className="px-10 py-8">
                                <span className="text-sm font-black text-gray-800 bg-gray-50 px-4 py-2 rounded-xl group-hover:bg-gray-900 group-hover:text-white transition-all">
                                  {po.POId || "PO-PENDING"}
                                </span>
                              </td>
                              <td className="px-10 py-8">
                                <p className="text-sm font-black text-gray-800 uppercase tracking-tight group-hover:text-orange-600 transition-colors">{po.vendorName}</p>
                                <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mt-1">Valuation: ${po.totalAmount?.toLocaleString()}</p>
                              </td>
                              <td className="px-10 py-8 text-[11px] text-gray-500 font-extrabold uppercase tracking-widest flex items-center gap-2">
                                <Truck size={14} className="text-gray-300" />
                                {po.deliveryLocation}
                              </td>
                              <td className="px-10 py-8 text-center sm:text-left">
                                <span className="px-4 py-2 rounded-xl bg-orange-50 text-orange-600 text-[9px] font-black uppercase tracking-widest border border-orange-100">{po.status}</span>
                              </td>
                              <td className="px-10 py-8 text-right">
                                <POActionDropdown po={po} onView={() => { setSelectedRequest(po); setViewModalOpen(true); }} onDownload={() => downloadPO(po)} />
                              </td>
                            </motion.tr>
                          )) : (
                            <tr>
                              <td colSpan="5" className="px-10 py-32 text-center">
                                <div className="flex flex-col items-center opacity-40">
                                  <ScrollText size={56} className="mb-6 text-gray-200" strokeWidth={1} />
                                  <p className="text-[11px] font-black uppercase tracking-[0.3em] text-gray-300">No Purchase Records Identified</p>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </PremiumCard>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </main>
      </div>

      {/* Modals with premium wrapper */}
      <Modal isOpen={activeModal === "rfq"} onClose={() => setActiveModal(null)} title="Request for Quotation" icon={FileText}>
        <CreateRFQ onClose={() => setActiveModal(null)} onSuccess={fetchRequests} />
      </Modal>
      <Modal isOpen={activeModal === "rfp"} onClose={() => setActiveModal(null)} title="Request for Proposal" icon={FileJson}>
        <CreateRFPForm onClose={() => setActiveModal(null)} onSuccess={fetchRequests} />
      </Modal>
      <Modal isOpen={activeModal === "boq"} onClose={() => setActiveModal(null)} title="Purchase Order" icon={ScrollText}>
        <BOQForm onClose={() => setActiveModal(null)} onSuccess={fetchRequests} />
      </Modal>

      <ViewFormModal isOpen={viewModalOpen} onClose={() => setViewModalOpen(false)} request={selectedRequest} />

      <Modal isOpen={proposalModalOpen} onClose={() => setProposalModalOpen(false)} title="Vendor Quotation Submission" icon={PlusCircle}>
        {selectedRFQ && <ProposalForm rfq={selectedRFQ} onClose={() => setProposalModalOpen(false)} onSuccess={handleProposalSuccess} />}
      </Modal>

      <Modal isOpen={viewProposalsModalOpen} onClose={() => setViewProposalsModalOpen(false)} title="Submission Analysis Architecture" icon={LayoutDashboard}>
        {selectedRFQ && <ViewProposals rfq={selectedRFQ} onClose={() => setViewProposalsModalOpen(false)} />}
      </Modal>

      {purchaseOrderModalOpen && selectedRFQ && (
        <PurchaseOrder rfq={selectedRFQ} onClose={() => setPurchaseOrderModalOpen(false)} onSuccess={handlePurchaseOrderSuccess} />
      )}
    </div>
  );
};

export default ProcurementDashboard;